import { useMemo } from 'react';

import memoizeOne from 'memoize-one';

import { EditorCardProvider } from '@atlaskit/editor-card-provider';
import { type ProductType } from '@atlaskit/linking-common';
import { useLinkPickerEditorProps } from '@atlassian/link-picker-plugins/editor';

import {
	type AkEditorLinkingProp,
	type CardOptions,
	type EditorLinkingProps,
	type LinkPickerOptions,
} from './types';

export const getSmartCardOptions = memoizeOne((product: ProductType) => {
	/**
	 * Supported Jira URLs and Jira Roadmap URLs, GDrive and ODFB will become Smart Links
	 * (only supports Cloud single-issue URLs right now)
	 */
	const smartLinksBeforeMacros =
		'jira,jiraroadmap,google-drive-sheets,google-drive-docs,OneDrive,google-drive-slides'.split(
			',',
		);

	// Make smart links connect to staging when developing locally.
	const environment = process.env.NODE_ENV === 'development' ? 'staging' : undefined;

	const cardProvider = Promise.resolve(new EditorCardProvider(environment, undefined, product));

	// IMPORTANT: If you change the code below, please also update the
	// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/pastePluginOptions.ts
	// platform/packages/editor/editor-presets-confluence/src/presets/full-page/pluginOptions/cardPluginOptions.ts
	return {
		provider: cardProvider,
		resolveBeforeMacros: smartLinksBeforeMacros,
		allowBlockCards: true,
		allowEmbeds: true,
	};
});

export const useSmartCards = (product: ProductType) => {
	return useMemo(() => {
		return getSmartCardOptions(product);
	}, [product]);
};

export const useLinkingOptions = (
	product: ProductType,
	cardOptions?: Partial<CardOptions>,
): AkEditorLinkingProp => {
	const baseSmartLinks = useSmartCards(product);

	return useMemo<AkEditorLinkingProp>(
		() => ({
			linking: {
				smartLinks: { ...baseSmartLinks, ...cardOptions },
			},
		}),
		[baseSmartLinks, cardOptions],
	);
};

export const useLinkingFeatures = (
	product: ProductType,
	options?: Partial<CardOptions>,
	cloudId?: string,
	linkPickerOptions?: LinkPickerOptions,
): AkEditorLinkingProp => {
	const config = useLinkingOptions(product, options);

	const productConfig = useMemo(
		() => ({
			product,
			cloudId,
		}),
		[product, cloudId],
	);

	const linkPickerProps = useLinkPickerEditorProps(undefined, productConfig);
	const linkPickerPlugins = linkPickerOptions?.linkPickerPlugins;

	return useMemo<AkEditorLinkingProp>(
		() => ({
			linking: {
				...config.linking,
				linkPicker: {
					...config.linking?.linkPicker,
					...linkPickerProps,
					...(linkPickerPlugins && { plugins: linkPickerPlugins }),
				},
				smartLinks: {
					...config.linking?.smartLinks,
				},
			},
		}),
		[config, linkPickerProps, linkPickerPlugins],
	);
};

/**
 * Component that provides linking features to editor via props
 */
export const EditorLinkingFeatures = ({
	children,
	options,
	cloudId,
	product = 'CONFLUENCE',
	linkPickerOptions,
}: EditorLinkingProps) => {
	const mergedProps = useLinkingFeatures(product, options, cloudId, linkPickerOptions);

	return children(mergedProps);
};
